import React, {useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {motion, AnimatePresence} from 'framer-motion'
import {useAuth} from '../../contexts/AuthContext'
import {useBrandingContext} from '../BrandingProvider'
import {useAnalytics} from '../../hooks/useAnalytics'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiMenu, FiX, FiUser, FiSettings, FiLogOut, FiBookOpen, FiHome, FiGrid} = FiIcons

const Header = () => {
  const {user, profile, signOut, isAdmin} = useAuth()
  const {branding} = useBrandingContext()
  const {trackCTAClick} = useAnalytics()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
    setIsProfileOpen(false)
    trackCTAClick('sign_out', 'header')
  }

  const handleNavClick = (name, href) => {
    trackCTAClick(`nav_${name.toLowerCase()}`, 'header', href)
    setIsMenuOpen(false)
  }

  const navigation = [
    {name: 'Home', href: '/', icon: FiHome},
    {name: 'Courses', href: '/courses', icon: FiBookOpen},
  ]

  const userNavigation = user ? [
    {name: 'Dashboard', href: '/dashboard', icon: FiGrid},
    ...(isAdmin ? [{name: 'Admin', href: '/admin', icon: FiSettings}] : []),
  ] : []

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={() => handleNavClick('Home', '/')}
            className="flex items-center space-x-3"
          >
            {branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt="Empower Nutrition Logo"
                className="h-10 w-auto"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${branding?.primary_color || '#399696'}, ${branding?.secondary_color || '#a0cfbc'})`
                }}
              >
                <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-white" />
              </div>
            )}
            <span 
              className="text-xl font-semibold text-gray-900"
              style={{fontFamily: `var(--font-heading, ${branding?.heading_font || 'Inter'})`}}
            >
              Empower Nutrition
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleNavClick(item.name, item.href)}
                className="flex items-center space-x-2 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors hover:text-white"
                style={{
                  '--hover-bg': branding?.primary_color || '#399696'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = branding?.primary_color || '#399696'
                  e.target.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent'
                  e.target.style.color = '#374151'
                }}
              >
                <SafeIcon icon={item.icon} className="w-4 h-4" />
                <span>{item.name}</span>
              </Link>
            ))}

            {user ? (
              <>
                {userNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => handleNavClick(item.name, item.href)}
                    className="flex items-center space-x-2 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors hover:text-white"
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = branding?.primary_color || '#399696'
                      e.target.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent'
                      e.target.style.color = '#374151'
                    }}
                  >
                    <SafeIcon icon={item.icon} className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                ))}

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{backgroundColor: branding?.secondary_color || '#a0cfbc'}}
                    >
                      <SafeIcon 
                        icon={FiUser} 
                        className="w-4 h-4"
                        style={{color: branding?.primary_color || '#399696'}}
                      />
                    </div>
                    <span className="hidden lg:block">{profile?.name}</span>
                  </button>

                  <AnimatePresence>
                    {isProfileOpen && (
                      <motion.div
                        initial={{opacity: 0, scale: 0.95}}
                        animate={{opacity: 1, scale: 1}}
                        exit={{opacity: 0, scale: 0.95}}
                        className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
                      >
                        <button
                          onClick={handleSignOut}
                          className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                          <span>Sign out</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  onClick={() => handleNavClick('Sign in', '/login')}
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => handleNavClick('Get Started', '/register')}
                  className="text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: branding?.primary_color || '#399696',
                    ':hover': {backgroundColor: branding?.accent_color || '#ffe8ad', color: branding?.primary_color || '#399696'}
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = branding?.accent_color || '#ffe8ad'
                    e.target.style.color = branding?.primary_color || '#399696'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = branding?.primary_color || '#399696'
                    e.target.style.color = 'white'
                  }}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{opacity: 0, height: 0}}
              animate={{opacity: 1, height: 'auto'}}
              exit={{opacity: 0, height: 0}}
              className="md:hidden border-t border-gray-200"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {[...navigation, ...userNavigation].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => handleNavClick(item.name, item.href)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-white hover:bg-gray-100 block px-3 py-2 rounded-md text-base font-medium transition-colors"
                    style={{'--hover-bg': branding?.primary_color || '#399696'}}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = branding?.primary_color || '#399696'
                      e.target.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent'
                      e.target.style.color = '#374151'
                    }}
                  >
                    <SafeIcon icon={item.icon} className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                ))}

                {user ? (
                  <button
                    onClick={handleSignOut}
                    className="flex items-center space-x-2 w-full text-left text-gray-700 hover:text-white hover:bg-gray-100 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = branding?.primary_color || '#399696'
                      e.target.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent'
                      e.target.style.color = '#374151'
                    }}
                  >
                    <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                ) : (
                  <div className="space-y-2 pt-2 border-t border-gray-200">
                    <Link
                      to="/login"
                      onClick={() => handleNavClick('Sign in', '/login')}
                      className="block text-gray-700 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => handleNavClick('Get Started', '/register')}
                      className="block text-white px-3 py-2 rounded-md text-base font-medium transition-colors"
                      style={{backgroundColor: branding?.primary_color || '#399696'}}
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}

export default Header